import { type PayloadAction, type Slice, createSlice } from "@reduxjs/toolkit"
// eslint-disable-next-line
import type { WritableDraft } from "immer"
import concat from "lodash-es/concat"
import eq from "lodash-es/eq"
import filter from "lodash-es/filter"
import forEach from "lodash-es/forEach"
import includes from "lodash-es/includes"
import uniq from "lodash-es/uniq"

import { type TTranslationKeys } from "$/i18n/i18n"

import { EChapterComponentType, type IChapterComponentModel } from "@/3514/types"
import { type TEmptyCallback, emptyCallback } from "@/shared/types/functions"

import { participantChapterUiConfig as uiConfig } from "../config"

interface TParticipantChapterButtonSetState {
    isForwardButtonDisabled: boolean
    isPreviousButtonVisible: boolean
    previousButtonLabelKey: keyof TTranslationKeys
    forwardButtonLabelKey: keyof TTranslationKeys
    previousButtonAction: TEmptyCallback
    forwardButtonAction: TEmptyCallback
}

interface TParticipantChapterContentState {
    isChapterSubmitted: boolean
    components: IChapterComponentModel[]
    incompleteComponentsIds: string[]
}

interface TState {
    buttonSet: TParticipantChapterButtonSetState
    content: TParticipantChapterContentState
}

const PARTICIPANT_CHAPTER_REDUCER_NAME = "participantChapter"

const initialState: TState = {
    buttonSet: {
        isForwardButtonDisabled: true,
        isPreviousButtonVisible: true,
        previousButtonLabelKey: "participantSide.chapter.previousButton.label",
        forwardButtonLabelKey: "participantSide.chapter.forwardButton.next.label",
        previousButtonAction: emptyCallback,
        forwardButtonAction: emptyCallback
    },
    content: { isChapterSubmitted: false, components: [], incompleteComponentsIds: [] }
}

const participantChapterContentSlice: Slice<TState> = createSlice({
    name: PARTICIPANT_CHAPTER_REDUCER_NAME,
    initialState,
    reducers: {
        resetState(state: WritableDraft<TState>): WritableDraft<TState> {
            state = initialState
            return state
        },

        // button set
        enableForwardButton(state: WritableDraft<TState>): void {
            state.buttonSet.isForwardButtonDisabled = false
        },
        disableForwardButton(state: WritableDraft<TState>): void {
            state.buttonSet.isForwardButtonDisabled = true
        },
        hidePreviousButton(state: WritableDraft<TState>): void {
            state.buttonSet.isPreviousButtonVisible = false
        },
        setForwardButtonLabel(state: WritableDraft<TState>, action: PayloadAction<keyof TTranslationKeys>): void {
            state.buttonSet.forwardButtonLabelKey = action.payload
        },
        setPreviousButtonAction(state: WritableDraft<TState>, action: PayloadAction<TEmptyCallback>): void {
            state.buttonSet.previousButtonAction = action.payload
        },
        setForwardButtonAction(state: WritableDraft<TState>, action: PayloadAction<TEmptyCallback>): void {
            state.buttonSet.forwardButtonAction = action.payload
        },

        // content
        initComponents(
            state: WritableDraft<TState>,
            action: PayloadAction<{ components: IChapterComponentModel[]; isChapterCompleted: boolean }>
        ): void {
            const _incompleteComponentsIds: string[] = []

            forEach<IChapterComponentModel>(action.payload.components, (item: IChapterComponentModel): void => {
                if (
                    item.isPublished &&
                    item.isRequired &&
                    includes<EChapterComponentType>(uiConfig.SUPPORTED_COMPONENT_TYPES, item.type) &&
                    includes<EChapterComponentType>(uiConfig.VALIDATED_COMPONENT_TYPES, item.type)
                ) {
                    _incompleteComponentsIds.push(item.uuid)
                }
            })

            state.content.components = action.payload.components
            state.content.isChapterSubmitted = action.payload.isChapterCompleted
            state.content.incompleteComponentsIds = _incompleteComponentsIds
        },
        completeComponent(
            state: WritableDraft<TState>,
            { payload }: PayloadAction<{ id: string; forceInclude: boolean }>
        ): WritableDraft<TState> {
            state.content.incompleteComponentsIds = uniq(
                payload.forceInclude
                    ? concat(state.content.incompleteComponentsIds, payload.id)
                    : filter(state.content.incompleteComponentsIds, (incId: string): boolean => !eq(incId, payload.id))
            )

            return state
        },
        completeChapter(state: WritableDraft<TState>): void {
            state.content.isChapterSubmitted = true
        }
    }
})

const { reducer: participantChapterReducer, actions: participantChapterActions }: Slice<TState> =
    participantChapterContentSlice

export { participantChapterReducer, participantChapterActions, PARTICIPANT_CHAPTER_REDUCER_NAME }
export type { TParticipantChapterButtonSetState, TParticipantChapterContentState }
